import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ApplePayExpressButton } from '@/components/ApplePayExpressButton/ApplePayExpressButton';
import { ShoppingBag } from '@/components/ShoppingBag';
import type { CheckoutFragment, MoneyFragment } from '@/graphql/saleor/api';
import {
  COUNTRYCODE_TO_SHOW_SHIPPING_AND_TAXNOTE_MESSAGE,
  GLOBAL_E_MARKET_AVAILABLE,
  ProductTypeSlug,
  VOUCHER_TYPE,
} from '@/lib/const';
import { type PreOrderData } from '@/lib/preOrder';
import { checkIfVatIncluded } from '@/lib/regions';
import type { ApiError, ApiErrorType } from '@/lib/types';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { PaypalExpressButton } from '../PaypalExpressButton/PaypalExpressButton';
import type { OrderDataFragment } from './TransactionSummary.utils';
import {
  calculateSubtotal,
  formatCheckoutOrder,
  getCheckoutToPayLater,
  getDepositPrice,
  getOrderToPayLater,
  getReverseDiscount,
  isOrder,
} from './TransactionSummary.utils';

export interface TransactionSummaryProps {
  areBraceletSizeErrorsSet?: () => boolean;
  channel?: string;
  coSalesTax?: MoneyFragment;
  code?: string;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
  fragment?: Partial<CheckoutFragment> | OrderDataFragment | null;
  isCheckoutSelectedAddress?: boolean;
  isColoradoBilling?: boolean;
  isDeposit?: boolean;
  isProcessing?: boolean;
  preOrderData?: PreOrderData | null;
  salesTax?: MoneyFragment;
  showButton?: boolean;
  showExpressButton?: boolean;
  showGiftBox?: boolean;
  showPadding?: boolean;
  showPaymentMethodsSection?: boolean;
  showSubtotalAndTaxes?: boolean;
  showTax: boolean;
  showTaxCalculationInfo?: boolean;
  voucherType?: string;
}

export const TransactionSummary: FC<TransactionSummaryProps> = ({
  areBraceletSizeErrorsSet,
  channel,
  coSalesTax,
  code,
  errorHandler,
  fragment,
  isCheckoutSelectedAddress = true,
  isColoradoBilling = false,
  isDeposit = true,
  isProcessing = false,
  preOrderData,
  salesTax,
  showButton = false,
  showExpressButton = false,
  showGiftBox = false,
  showPadding = true,
  showPaymentMethodsSection = true,
  showSubtotalAndTaxes = true,
  showTax = true,
  showTaxCalculationInfo,
  voucherType,
}) => {
  const formCtx = useForm();
  const { country, locale } = useRegionProvider();
  const {
    applepayExpressPaymentMax,
    applepayExpressPaymentMin,
    paypalExpressPaymentMax,
    paypalExpressPaymentMin,
  } = useConfigProvider();

  if (!fragment) return null;
  const {
    discount,
    grossTotalPrice,
    netTotalPrice,
    shippingPrice,
    subtotal,
    subtotalGross,
    totalAuthorized,
  } = formatCheckoutOrder(fragment);
  const isPartiallyPaid =
    totalAuthorized &&
    Boolean(grossTotalPrice?.amount - totalAuthorized?.amount !== 0);
  const depositPrice = getDepositPrice(fragment, preOrderData);

  const toPayLater = isOrder(fragment)
    ? getOrderToPayLater(fragment, preOrderData)
    : getCheckoutToPayLater(fragment, preOrderData);

  const subtotalWithDiscount = calculateSubtotal(
    checkIfVatIncluded(channel) ? subtotalGross : subtotal,
    discount,
    voucherType,
  );

  const hasWatch = !!fragment.lines?.some(
    (line) => line.variant?.product.productType.name === ProductTypeSlug.Watch,
  );

  const totalPrice = showTax ? grossTotalPrice : netTotalPrice;

  const displayPaypalExpressButton =
    !GLOBAL_E_MARKET_AVAILABLE.includes(`${country.slug}-${locale.slug}`) &&
    showExpressButton &&
    netTotalPrice &&
    areBraceletSizeErrorsSet &&
    (paypalExpressPaymentMax === null ||
      netTotalPrice.amount <= (paypalExpressPaymentMax ?? 0)) &&
    (paypalExpressPaymentMin === null ||
      netTotalPrice.amount >= (paypalExpressPaymentMin ?? 0));

  const displayApplePayExpressButton =
    showButton &&
    showExpressButton &&
    netTotalPrice &&
    areBraceletSizeErrorsSet &&
    (applepayExpressPaymentMax === null ||
      netTotalPrice.amount <= (applepayExpressPaymentMax ?? 0)) &&
    (applepayExpressPaymentMin === null ||
      netTotalPrice.amount >= (applepayExpressPaymentMin ?? 0));

  return (
    <ShoppingBag.SummaryWrapper
      data-testid='ShoppingBagSummaryWrapper'
      showPadding={showPadding}
    >
      <FormProvider {...formCtx} data-test-id='ShoppingBagFormProvider'>
        {showSubtotalAndTaxes && (
          <>
            <ShoppingBag.SubtotalPrice
              data-testid='ShoppingBagSubtotalPrice'
              price={subtotalWithDiscount}
            />

            {voucherType === VOUCHER_TYPE.FIXED &&
              discount &&
              discount.amount > 0 && (
                <ShoppingBag.Discount
                  data-testid='ShoppingBagDiscount'
                  price={getReverseDiscount(discount)}
                />
              )}

            <ShoppingBag.ShippingPrice
              data-testid='ShoppingBagShippingPrice'
              price={shippingPrice}
            />

            {showTax && (
              <ShoppingBag.SalesTax
                channel={channel}
                data-testid='ShoppingBagSalesTax'
                displayZero={true}
                price={salesTax}
              />
            )}
            {isColoradoBilling && coSalesTax?.amount !== 0 && (
              <ShoppingBag.RetailDeliveryFeesTax
                data-testid='ShoppingBagRetailDeliveryFeesTax'
                price={coSalesTax}
              />
            )}
          </>
        )}
        <ShoppingBag.TotalPrice
          channel={channel}
          data-testid='ShoppingBagTotalPrice'
          isCheckoutSelectedAddress={isCheckoutSelectedAddress}
          netPrice={netTotalPrice}
          price={totalPrice}
          withBottomBorder={!!preOrderData}
          withTaxCalculationInfo={showTaxCalculationInfo}
        />
        {isPartiallyPaid && (
          <ShoppingBag.AlreadyPaidAmount
            data-testid='ShoppingBagDeposit'
            price={totalAuthorized}
            type='already paid'
          />
        )}
        {preOrderData &&
          (isDeposit ? (
            <>
              <ShoppingBag.ToPayLater
                data-testid='ShoppingBagToPayLater'
                price={toPayLater}
              />
              <ShoppingBag.Deposit
                data-testid='ShoppingBagDeposit'
                depositPercentage={preOrderData.deposit_percentage}
                price={depositPrice}
                type='already paid'
              />
              <ShoppingBag.PreOrderNote />
            </>
          ) : (
            isOrder(fragment) &&
            toPayLater && (
              <>
                <ShoppingBag.Deposit
                  data-testid='ShoppingBagOrderSummary-DepositAlreadyPaid'
                  depositPercentage={preOrderData?.deposit_percentage}
                  price={depositPrice}
                  type='already paid'
                />
                <ShoppingBag.ToPayNow
                  data-testid='ShoppingBagOrderSummary-FullPaidMoney'
                  price={toPayLater}
                />
              </>
            )
          ))}
        {code &&
          code ===
            COUNTRYCODE_TO_SHOW_SHIPPING_AND_TAXNOTE_MESSAGE.CountryCode && (
            <ShoppingBag.ShippingAndTaxNote data-testid='ShoppingBagShippingAndTaxNote' />
          )}
        {showGiftBox && <ShoppingBag.Gift />}
        {showButton && areBraceletSizeErrorsSet && !isOrder(fragment) && (
          <ShoppingBag.Buttons
            areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
            checkoutId={fragment.id}
            coupon={fragment.voucherCode ?? undefined}
            data-testid='ShoppingBagViewButtons'
            isDisabled={isProcessing}
            lines={fragment.lines}
            totalBalance={Math.abs(fragment.totalBalance?.amount as number)}
          />
        )}
        {displayApplePayExpressButton && (
          <ApplePayExpressButton
            areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
            data-test-id='ShoppingBagDrawerAppleExpressButton'
            errorHandler={errorHandler}
          />
        )}
        {displayPaypalExpressButton && (
          <PaypalExpressButton
            areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
            data-test-id='ShoppingBagDrawerPaypalExpressButton'
            errorHandler={errorHandler}
            zIndex={1}
          />
        )}
        {showPaymentMethodsSection && (
          <ShoppingBag.PaymentMethodsSection
            billingAddressCountryCode={fragment.billingAddress?.country.code}
            data-testid='ShoppingBagPaymentMethodsSection'
            hasWatch={hasWatch}
            id={fragment?.id}
            isDepositPaid={!isDeposit}
            isPreOrder={!!preOrderData}
            totalPrice={grossTotalPrice?.amount}
          />
        )}
      </FormProvider>
    </ShoppingBag.SummaryWrapper>
  );
};
